/**
 * Hook centralise pour la detection de visibilite et resume de l'application
 *
 * Gere tous les scenarios de retour utilisateur :
 * - Changement d'onglet (visibilitychange)
 * - Retour depuis l'historique navigateur (pageshow avec bfcache)
 * - Focus de fenetre (focus)
 * - Reconnexion reseau (online)
 * - Resume PWA iOS (pageshow + standalone)
 *
 * @module useAppVisibility
 * @author Equipe Brydge
 * @since 2.0.0
 */

import {useEffect, useCallback, useRef, useState} from 'react';

/**
 * Delai de debounce par defaut pour eviter les revalidations multiples
 * @constant {number}
 */
const DEFAULT_DEBOUNCE_MS = 300;

/**
 * Detecte si l'application tourne en mode PWA standalone
 * @return {boolean}
 */
const isPWAStandalone = () => {
  if (typeof window === 'undefined') return false;

  // iOS Safari PWA
  if (window.navigator.standalone === true) return true;

  // Android/Desktop PWA via display-mode
  if (window.matchMedia('(display-mode: standalone)').matches) return true;

  return false;
};

/**
 * Hook pour detecter les changements de visibilite de l'application
 * et declencher des callbacks lors du retour de l'utilisateur
 *
 * Ce hook est essentiel pour garantir la fraicheur des donnees dans les PWA,
 * particulierement sur iOS ou les apps en background gardent leur etat en memoire.
 *
 * @param {Function} onVisible - Callback execute quand l'app redevient visible
 * @param {Object} options - Options de configuration
 * @param {boolean} [options.enabled=true] - Active/desactive le hook
 * @param {number} [options.debounceMs=300] - Delai de debounce en millisecondes
 * @param {boolean} [options.includeOnline=true] - Inclure l'evenement online (reconnexion reseau)
 * @param {boolean} [options.includeFocus=true] - Inclure l'evenement focus de fenetre
 * @param {boolean} [options.includeVisibility=true] - Inclure l'evenement visibilitychange (changement d'onglet)
 * @return {Object} Etat de visibilite {isVisible, lastVisibleAt, isPWA}
 *
 * @example
 * // Revalider les donnees SWR au retour sur l'application
 * const {isVisible} = useAppVisibility(() => {
 *   mutate(); // Force revalidation des donnees
 * });
 *
 * @example
 * // Utilisation conditionnelle avec debounce personnalise
 * useAppVisibility(
 *   () => { refreshData(); },
 *   { enabled: isAuthenticated, debounceMs: 500 }
 * );
 */
export const useAppVisibility = (onVisible, options = {}) => {
  const {
    enabled = true,
    debounceMs = DEFAULT_DEBOUNCE_MS,
    includeOnline = true,
    includeFocus = true,
    includeVisibility = true,
  } = options;

  const [isVisible, setIsVisible] = useState(true);
  const [lastVisibleAt, setLastVisibleAt] = useState(Date.now());
  const debounceTimeoutRef = useRef(null);
  const lastTriggerRef = useRef(0);

  /**
   * Fonction de callback debouncee pour eviter les appels multiples
   * lors de transitions rapides de visibilite
   */
  const debouncedCallback = useCallback(() => {
    const now = Date.now();

    // Eviter les appels trop rapproches (protection supplementaire)
    if (now - lastTriggerRef.current < debounceMs) {
      return;
    }

    // Annuler le timeout precedent si existant
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }

    // Planifier l'execution du callback
    debounceTimeoutRef.current = setTimeout(() => {
      lastTriggerRef.current = Date.now();
      setLastVisibleAt(Date.now());

      if (onVisible && typeof onVisible === 'function') {
        onVisible({
          timestamp: Date.now(),
          isPWA: isPWAStandalone(),
        });
      }
    }, debounceMs);
  }, [onVisible, debounceMs]);

  useEffect(() => {
    if (!enabled || typeof window === 'undefined') return;

    /**
     * Gestionnaire de changement de visibilite de l'onglet
     * Declenche par : changement d'onglet, minimisation de fenetre
     */
    const handleVisibilityChange = () => {
      const visible = !document.hidden;
      setIsVisible(visible);

      if (visible) {
        debouncedCallback();
      }
    };

    /**
     * Gestionnaire de l'evenement pageshow
     * Declenche par : navigation back/forward, restoration depuis bfcache
     * Critique pour les PWA iOS qui gardent l'etat en memoire
     *
     * @param {PageTransitionEvent} event - L'evenement pageshow, contenant des informations sur la restoration de la page
     */
    const handlePageShow = (event) => {
      // event.persisted indique une restoration depuis bfcache
      // Particulierement important pour iOS PWA qui garde l'app en memoire
      if (event.persisted || isPWAStandalone()) {
        debouncedCallback();
      }
    };

    /**
     * Gestionnaire du focus fenetre
     * Complement a visibilitychange pour les cas specifiques
     * ou l'app revient au premier plan sans changement de visibilite
     */
    const handleWindowFocus = () => {
      if (!document.hidden) {
        debouncedCallback();
      }
    };

    /**
     * Gestionnaire de reconnexion reseau
     * Revalide les donnees apres une periode hors ligne
     */
    const handleOnline = () => {
      debouncedCallback();
    };

    // Enregistrement des ecouteurs d'evenements
    if (includeVisibility) {
      document.addEventListener('visibilitychange', handleVisibilityChange);
      window.addEventListener('pageshow', handlePageShow);
    }

    if (includeFocus) {
      window.addEventListener('focus', handleWindowFocus);
    }

    if (includeOnline) {
      window.addEventListener('online', handleOnline);
    }

    // Nettoyage lors du demontage
    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }

      if (includeVisibility) {
        document.removeEventListener('visibilitychange', handleVisibilityChange);
        window.removeEventListener('pageshow', handlePageShow);
      }

      if (includeFocus) {
        window.removeEventListener('focus', handleWindowFocus);
      }

      if (includeOnline) {
        window.removeEventListener('online', handleOnline);
      }
    };
  }, [enabled, debouncedCallback, includeOnline, includeFocus, includeVisibility]);

  return {
    isVisible,
    lastVisibleAt,
    isPWA: isPWAStandalone(),
  };
};

export default useAppVisibility;
